import React, { useState, useEffect } from 'react';
import { Typography, Card, List, Button, Badge, Empty, message, Tag } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { notificationService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './CustomerDashboard.css';

const { Title, Paragraph, Text } = Typography;

const Notifications = () => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationService.getNotifications();
      const data = response.data?.data?.data || response.data?.data || [];
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.data?.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        (Array.isArray(prev) ? prev : []).map((notif) =>
          notif.id === notificationId
            ? { ...notif, read_at: new Date().toISOString(), is_read: true }
            : notif,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      message.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) =>
        (Array.isArray(prev) ? prev : []).map((notif) => ({
          ...notif,
          read_at: notif.read_at || new Date().toISOString(),
          is_read: true,
        })),
      );
      setUnreadCount(0);
      message.success('All notifications marked as read');
    } catch (error) {
      message.error('Failed to mark all notifications as read');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      order: '📦',
      payment: '💳',
      promotion: '🎉',
      system: '⚙️',
      delivery: '🚚',
      review: '⭐',
    };
    return icons[type] || '📢';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification) => {
    if (!(notification.read_at || notification.is_read)) {
      handleMarkAsRead(notification.id);
    }
    const data =
      typeof notification.data === 'string'
        ? JSON.parse(notification.data)
        : notification.data || {};
    switch (notification.type) {
      case 'message':
        navigate('/chat');
        break;
      case 'order':
        navigate(data.order_id ? `/tracking/${data.order_id}` : '/orders');
        break;
      case 'delivery':
        navigate(data.order_id ? `/tracking/${data.order_id}` : '/orders');
        break;
      case 'payment':
        navigate('/wallet');
        break;
      case 'promotion':
        navigate('/products');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="customer-page" style={{ padding: '0' }}>
        <div className="customer-page-header">
          <div
            className="customer-card-loading"
            style={{ height: 24, width: 160, marginBottom: 8 }}
          />
          <div className="customer-card-loading" style={{ height: 16, width: 240 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="customer-page" style={{ padding: '0' }}>
      <div className="customer-page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Notifications
              {unreadCount > 0 && (
                <Badge
                  count={unreadCount}
                  color="#52b788"
                  style={{ marginLeft: 8, marginTop: -4 }}
                />
              )}
            </Title>
            <Paragraph style={{ marginTop: 6 }}>
              Stay updated with your orders, payments, and promotions.
            </Paragraph>
          </div>
          {unreadCount > 0 && (
            <Button
              className="customer-btn-primary"
              icon={<CheckOutlined />}
              onClick={handleMarkAllAsRead}
              style={{ height: 36, fontSize: 13 }}
            >
              Mark All Read
            </Button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <Card className="customer-card">
          <Empty description="No notifications yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      ) : (
        <List
          dataSource={notifications}
          renderItem={(notification, idx) => (
            <List.Item
              className="animate-fade-slide-up"
              onClick={() => handleNotificationClick(notification)}
              style={{
                opacity: 0,
                animationDelay: `${idx * 0.05}s`,
                backgroundColor:
                  notification.read_at || notification.is_read ? 'transparent' : '#fafff5',
                borderRadius: 12,
                padding: '16px 20px',
                marginBottom: 8,
                border:
                  notification.read_at || notification.is_read
                    ? '1px solid #e8f0e8'
                    : '1px solid #d4edd7',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
              }}
              actions={[
                !(notification.read_at || notification.is_read) && (
                  <Button
                    type="text"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(notification.id);
                    }}
                    style={{ color: '#52b788', fontWeight: 500 }}
                  >
                    Mark as read
                  </Button>
                ),
              ]}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      fontSize: 28,
                      textAlign: 'center',
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background:
                        notification.read_at || notification.is_read ? '#f3f4f6' : '#e8f5e9',
                      borderRadius: 12,
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </div>
                }
                title={
                  <div>
                    <Text strong style={{ color: '#2d5a3d' }}>
                      {notification.title}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      <Tag
                        className="customer-tag-green"
                        style={{ fontSize: 11, padding: '1px 8px' }}
                      >
                        {notification.type}
                      </Tag>
                    </div>
                  </div>
                }
                description={
                  <div>
                    <Text style={{ color: '#4b5563' }}>{notification.message}</Text>
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatTime(notification.created_at)}
                      </Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

export default Notifications;
